import React, { useState } from "react";
import { Shield, AlertTriangle, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";

interface ChatSafetyNoticeDialogProps {
  isOpen: boolean;
  onAccept: () => void;
}

const ChatSafetyNoticeDialog: React.FC<ChatSafetyNoticeDialogProps> = ({
  isOpen,
  onAccept,
}) => {
  const { t } = useTranslation();
  const [hasAgreed, setHasAgreed] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (hasAgreed) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white text-center">
            {t("chatSafetyNotice.title")}
          </h2>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-16rem)]">
          <div className="space-y-6">
            {/* Important Notice Banner */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">
                    {t("chatSafetyNotice.importantNotice")}
                  </h3>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    {t("chatSafetyNotice.noticeDescription")}
                  </p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                {t("chatSafetyNotice.mainMessage")}
              </p>

              {/* Security Points */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3 mb-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">
                      {t("chatSafetyNotice.securityTitle")}
                    </h4>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>{t("chatSafetyNotice.securityPoint1")}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>{t("chatSafetyNotice.securityPoint2")}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold">•</span>
                        <span>{t("chatSafetyNotice.securityPoint3")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed">
                {t("chatSafetyNotice.closingMessage")}
              </p>
            </div>

            {/* Agreement Checkbox */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={hasAgreed}
                  onChange={(e) => setHasAgreed(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-900 select-none group-hover:text-blue-600 transition-colors">
                  {t("chatSafetyNotice.agreementText")}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {t("chatSafetyNotice.footerNote")}
          </p>
          <Button
            onClick={handleAccept}
            disabled={!hasAgreed}
            className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${
              hasAgreed
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {t("chatSafetyNotice.acceptButton")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatSafetyNoticeDialog;


